import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, UserCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import DharmaWheel from "./DharmaWheel";
import LanguageSwitcher from "./LanguageSwitcher";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/dhamma", label: "Dhamma" },
  { to: "/reformers", label: "Reformers" },
  { to: "/events", label: "Events" },
  { to: "/newsletter", label: "Newsletter" },
  { to: "/platform", label: "Platform", highlight: true },
  { to: "/children", label: "Children" },
  { to: "/education", label: "Education" },
  { to: "/membership", label: "Membership" },
  { to: "/donate", label: "Donate" },
];

export function Header() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-pearl/80 backdrop-blur-xl border-b border-navy/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-4">
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo-home-link">
            <DharmaWheel size={40} color="#CFA052" ringColor="#001B3A" />
            <div className="hidden sm:block leading-tight">
              <div className="font-display text-base xl:text-xl text-navy font-semibold whitespace-nowrap">Ambedkarite Buddhist Global Network UK</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-saffron whitespace-nowrap">Rooted in Dhamma · Inspired by Babasaheb</div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-6 2xl:gap-8">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  l.highlight
                    ? `text-sm tracking-wide whitespace-nowrap transition-colors px-2.5 py-1 rounded-md border ${isActive ? "bg-saffron text-navy border-saffron" : "border-saffron/40 text-saffron hover:bg-saffron hover:text-navy hover:border-saffron"}`
                    : `text-sm tracking-wide whitespace-nowrap transition-colors ${isActive ? "text-saffron" : "text-navy/80 hover:text-navy"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
            <LanguageSwitcher />
            {user ? (
              <>
                <Link
                  to="/profile"
                  data-testid="header-profile-link"
                  className="text-sm text-navy/80 hover:text-navy flex items-center gap-2 whitespace-nowrap"
                >
                  <UserCircle2 size={18} />
                  {user.name?.split(" ")[0] || "Profile"}
                </Link>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="header-admin-link"
                    className="text-sm text-saffron hover:text-saffron-light whitespace-nowrap"
                  >
                    Admin
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  data-testid="header-logout-btn"
                  className="text-sm text-navy/70 hover:text-saffron inline-flex items-center gap-1 whitespace-nowrap"
                >
                  <LogOut size={16} /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="header-login-link" className="text-sm text-navy/80 hover:text-navy whitespace-nowrap">
                  Login
                </Link>
                <Link
                  to="/membership"
                  data-testid="header-join-cta"
                  className="px-5 py-2.5 bg-navy text-pearl text-sm tracking-wide hover:bg-navy-light transition-colors rounded-sm whitespace-nowrap"
                >
                  Join ABGN-UK
                </Link>
              </>
            )}
          </div>

          <div className="xl:hidden flex items-center gap-2">
            <LanguageSwitcher compact />
            <button
              onClick={() => setOpen((s) => !s)}
              className="p-2 text-navy"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {open && (
          <div className="xl:hidden pb-6 pt-2 space-y-3 border-t border-navy/10">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="block text-navy/80 text-sm py-1"
              >
                {l.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-navy/10 flex items-center gap-3">
              {user ? (
                <>
                  <Link
                    to={user.role === "admin" ? "/admin" : "/dashboard"}
                    onClick={() => setOpen(false)}
                    className="text-sm text-navy"
                    data-testid="mobile-dashboard-link"
                  >
                    Dashboard
                  </Link>
                  <button onClick={handleLogout} className="text-sm text-saffron" data-testid="mobile-logout-btn">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="text-sm text-navy" data-testid="mobile-login-link">
                    Login
                  </Link>
                  <Link
                    to="/membership"
                    onClick={() => setOpen(false)}
                    className="text-sm px-4 py-2 bg-navy text-pearl rounded-sm"
                    data-testid="mobile-join-cta"
                  >
                    Join ABGN-UK
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export function Footer() {
  const [charity, setCharity] = useState(null);
  useEffect(() => {
    api.get("/charity/status")
      .then((r) => setCharity(r.data))
      .catch(() => setCharity(null));
  }, []);

  const isRegistered = charity?.is_registered;
  const badgeLabel = isRegistered
    ? `Registered charity no. ${charity.charity_number}`
    : "CIO application in preparation";

  return (
    <footer className="bg-navy text-pearl/80 mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3 mb-5">
            <DharmaWheel size={44} color="#CFA052" ringColor="#FDFBF7" />
            <div>
              <div className="font-display text-xl text-pearl">Ambedkarite Buddhist Global Network UK</div>
              <div
                className={`text-[10px] uppercase tracking-[0.25em] ${isRegistered ? "text-green-300" : "text-gold"}`}
                data-testid="footer-charity-status"
              >
                {badgeLabel}
              </div>
            </div>
          </div>
          <p className="text-sm leading-relaxed text-pearl/70 max-w-md">
            A charitable public-benefit Buddhist network advancing Ambedkarite
            Buddhist teachings, education, meditation, equality, inclusion,
            compassion, community wellbeing and accessible participation
            through physical events and our own charitable digital platform.
          </p>
          <p className="mt-6 text-xs italic text-gold">
            Educate &middot; Meditate &middot; Empower &middot; Elevate
          </p>
          <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-pearl/50">
            Rooted in Dhamma · Inspired by Babasaheb · Committed to humanity
          </p>
        </div>
        <div className="md:col-span-3">
          <h4 className="text-pearl text-sm uppercase tracking-[0.2em] mb-4">Explore</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-gold">About</Link></li>
            <li><Link to="/launch" className="hover:text-gold">Launch · Posters & Video</Link></li>
            <li><Link to="/dhamma" className="hover:text-gold">The Dhamma</Link></li>
            <li><Link to="/reformers" className="hover:text-gold">Reformers</Link></li>
            <li><Link to="/children" className="hover:text-gold">Children's Corner</Link></li>
            <li><Link to="/education" className="hover:text-gold">Education Series</Link></li>
            <li><Link to="/events" className="hover:text-gold">Events</Link></li>
            <li><Link to="/newsletter" className="hover:text-gold">Newsletter</Link></li>
            <li><Link to="/membership" className="hover:text-gold">Membership</Link></li>
            <li><Link to="/volunteer" className="hover:text-gold" data-testid="footer-volunteer">Volunteer</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <h4 className="text-pearl text-sm uppercase tracking-[0.2em] mb-4">Governance</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/mission" className="hover:text-gold" data-testid="footer-mission">Mission & Vision</Link></li>
            <li><Link to="/public-benefit" className="hover:text-gold" data-testid="footer-pb">Public Benefit</Link></li>
            <li><Link to="/transparency" className="hover:text-gold" data-testid="footer-transparency">Transparency & Finances</Link></li>
            <li><Link to="/our-journey" className="hover:text-gold" data-testid="footer-journey">Our Journey</Link></li>
            <li><Link to="/impact" className="hover:text-gold" data-testid="footer-impact">Community Impact</Link></li>
            <li><Link to="/safeguarding" className="hover:text-gold" data-testid="footer-safe">Safeguarding</Link></li>
            <li><Link to="/equality-diversity" className="hover:text-gold" data-testid="footer-eandd">E & D</Link></li>
            <li><Link to="/code-of-conduct" className="hover:text-gold" data-testid="footer-coc">Code of Conduct</Link></li>
            <li><Link to="/conflict-of-interest" className="hover:text-gold" data-testid="footer-coi">Conflict of Interest</Link></li>
            <li><Link to="/privacy" className="hover:text-gold" data-testid="footer-privacy">Privacy (GDPR)</Link></li>
            <li><Link to="/complaints" className="hover:text-gold" data-testid="footer-complaints">Complaints</Link></li>
            <li><Link to="/constitution" className="hover:text-gold">Constitution</Link></li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <h4 className="text-pearl text-sm uppercase tracking-[0.2em] mb-4">Get in touch</h4>
          <p className="text-sm text-pearl/70 break-words">info@ambedkaritebuddhistglobalnetworkuk.com</p>
          <p className="text-sm text-pearl/70 mt-2">Operating across the United Kingdom</p>
          <div className="mt-6">
            <Link to="/donate" className="inline-block text-sm text-navy bg-gold hover:bg-gold-light px-5 py-2.5 rounded-sm" data-testid="footer-donate-cta">
              Support our mission
            </Link>
          </div>
        </div>
      </div>
      <div className="border-t border-pearl/10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-pearl/60">© {new Date().getFullYear()} Ambedkarite Buddhist Global Network UK. All rights reserved.</p>
          <p className="text-xs text-pearl/50 italic">Inspired by Babasaheb · United in Dhamma · Empowering Generations</p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-pearl">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
